import * as THREE from 'three';
import { type FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { type ThreeEvent } from '@react-three/fiber';
import debounce from 'debounce';
import type {
  InstanceProps,
  InstancedObjectMeshData,
  SphericalImageAnchorStyle,
  SphericalImageAnchorStyles,
  SphericalImageProps,
  SphericalImageSetExtendedProps,
} from '../../types';
import { InstancedObject } from '../InstancedObject';
import { ImageType } from '../Enums';
import { RENDER_ORDER_IMAGE_ANCHOR } from '../RenderOrder';
import { ImageBillboard } from '../ImageBillboard';

interface FinalSphericalImageAnchorStyle {
  color: number;
  opacity: number;
  size: number;
  emissiveIntensity: number;
}

interface FinalSphericalImageAnchorStyles {
  normal: FinalSphericalImageAnchorStyle;
  highlighted: FinalSphericalImageAnchorStyle;
  viewModeNormal: FinalSphericalImageAnchorStyle;
  viewModeHighlighted: FinalSphericalImageAnchorStyle;
}

const defaultStyles = {
  normal: {
    color: 0x7c94ff,
    size: 0.3,
    opacity: 0.5,
    emissiveIntensity: 0.1,
  },
  highlighted: {
    color: 0xff3d3d,
    size: 0.3,
    opacity: 0.6,
    emissiveIntensity: 0.3,
  },
  viewModeNormal: {
    color: 0x7c94ff,
    size: 0.15,
    opacity: 0.5,
    emissiveIntensity: 0.7,
  },
  viewModeHighlighted: {
    color: 0xff3d3d,
    size: 0.15,
    opacity: 0.6,
    emissiveIntensity: 0.7,
  },
};

const getStyleForMode = (
  styles: SphericalImageAnchorStyles | undefined,
  isSphericalViewMode: boolean,
  isHighlighted: boolean
): SphericalImageAnchorStyle | undefined =>
  isSphericalViewMode
    ? isHighlighted
      ? styles?.viewModeHighlighted
      : styles?.viewModeNormal
    : isHighlighted
      ? styles?.highlighted
      : styles?.normal;

const getFinalStyleForMode = (
  styles: FinalSphericalImageAnchorStyles,
  isSphericalViewMode: boolean,
  isHighlighted: boolean
): FinalSphericalImageAnchorStyle =>
  isSphericalViewMode
    ? isHighlighted
      ? styles?.viewModeHighlighted
      : styles?.viewModeNormal
    : isHighlighted
      ? styles?.highlighted
      : styles?.normal;

const getActualStyle = (
  defaultStyles: FinalSphericalImageAnchorStyles,
  groupStyles: SphericalImageAnchorStyles | undefined,
  imageStyles: SphericalImageAnchorStyles | undefined,
  isSphericalViewMode: boolean,
  isHighlighted: boolean
): FinalSphericalImageAnchorStyle => {
  const defaultStyle = getFinalStyleForMode(defaultStyles, isSphericalViewMode, isHighlighted);
  const groupStyle = getStyleForMode(groupStyles, isSphericalViewMode, isHighlighted);
  const imageStyle = getStyleForMode(imageStyles, isSphericalViewMode, isHighlighted);
  return {
    ...defaultStyle,
    ...groupStyle,
    ...imageStyle,
  };
};

export const InstancedSphericalImageSet: FC<SphericalImageSetExtendedProps> = memo(
  ({
    id,
    name,
    sphericalImages,
    styles: groupStyles,
    isSphericalViewMode,
    isVisible,
    visibilityBox,
    currentImage,
    setNextCurrentImage,
    anchorSphere,
  }) => {
    const [hoveredImage, setHoveredImage] = useState<SphericalImageProps>();

    const isInsideVisibilityBox = useCallback(
      (imageProps: SphericalImageProps) => {
        return visibilityBox.checkIsInside(imageProps.position);
      },
      [visibilityBox]
    );

    const shouldHandleClick = useCallback((event: ThreeEvent<PointerEvent>) => {
      const { altKey, ctrlKey, metaKey, shiftKey, button } = event.nativeEvent;
      const isLeftButton = button === 0;
      // double-click is not detected in useOnClick handler
      // const isDoubleClick = event.nativeEvent.detail === 2;
      if (
        !isLeftButton ||
        // isDoubleClick ||
        altKey ||
        ctrlKey ||
        metaKey ||
        shiftKey
        // !isSphericalClosestToCursor(event)
      ) {
        return false;
      }
      return true;
    }, []);

    const enterSphericalMode = useCallback(
      (image: SphericalImageProps) => {
        setHoveredImage(undefined);
        setNextCurrentImage({
          imageType: ImageType.Spherical,
          ...image,
        });
      },
      [setNextCurrentImage]
    );

    const debouncedEnterSphericalMode = debounce(enterSphericalMode, 300);

    const onClick = useCallback(
      (event: ThreeEvent<PointerEvent>, image: SphericalImageProps) => {
        // console.log('onClick', event, image);
        event.stopPropagation();
        debouncedEnterSphericalMode(image);
      },
      [debouncedEnterSphericalMode]
    );

    const anchorInstances: InstanceProps[] = useMemo(() => {
      return sphericalImages
        .filter((imageProps) => (isVisible ?? true) && isInsideVisibilityBox(imageProps))
        .map((image) => {
          const isCurrentImage = currentImage && currentImage.name === image.name;
          const { position, rotation, styles: imageStyles } = image;

          const normalStyle = getActualStyle(
            defaultStyles,
            groupStyles,
            imageStyles,
            isSphericalViewMode,
            false
          );

          const highlightedStyle = getActualStyle(
            defaultStyles,
            groupStyles,
            imageStyles,
            isSphericalViewMode,
            true
          );

          return {
            position,
            rotation,
            scale: isCurrentImage ? 0 : normalStyle.size,
            color: normalStyle.color,
            onClick: (event: ThreeEvent<PointerEvent>) => onClick(event, image),
            onPointerOver: () => {
              setHoveredImage(image);
              return {
                color: highlightedStyle.color,
                scale: highlightedStyle.size,
              };
            },
            onPointerOut: () => {
              setHoveredImage(undefined);
            },
          };
        });
    }, [
      sphericalImages,
      groupStyles,
      isVisible,
      isInsideVisibilityBox,
      isSphericalViewMode,
      currentImage,
      onClick,
    ]);

    const material = useMemo(() => {
      return new THREE.MeshPhongMaterial({
        transparent: true,
      });
    }, []);

    useEffect(() => {
      const isHighlighted = false;
      const defaultStyle = getFinalStyleForMode(defaultStyles, isSphericalViewMode, isHighlighted);
      const groupStyle = getStyleForMode(groupStyles, isSphericalViewMode, isHighlighted);

      const { opacity, color, emissiveIntensity } = { ...defaultStyle, ...groupStyle };
      // each instance will have it's own color
      // material.color.setHex(color);
      material.opacity = opacity;
      material.emissive.setHex(color);
      material.emissiveIntensity = emissiveIntensity;
      material.needsUpdate = true;
    }, [material, groupStyles, isSphericalViewMode]);

    useEffect(() => {
      if (!material) return () => {};
      return () => material.dispose();
    }, [material]);

    const highlightedMaterial = useMemo(() => {
      return new THREE.MeshPhongMaterial({
        transparent: true,
      });
    }, []);

    useEffect(() => {
      if (!hoveredImage) return;
      const isHighlighted = true;
      const defaultStyle = getFinalStyleForMode(defaultStyles, isSphericalViewMode, isHighlighted);
      const groupStyle = getStyleForMode(groupStyles, isSphericalViewMode, isHighlighted);
      const imageStyle = getStyleForMode(hoveredImage.styles, isSphericalViewMode, isHighlighted);

      const { opacity, color, emissiveIntensity } = {
        ...defaultStyle,
        ...groupStyle,
        ...imageStyle,
      };

      highlightedMaterial.color.setHex(color);
      highlightedMaterial.opacity = opacity;
      highlightedMaterial.emissive.setHex(color);
      highlightedMaterial.emissiveIntensity = emissiveIntensity;
      highlightedMaterial.needsUpdate = true;
    }, [highlightedMaterial, groupStyles, isSphericalViewMode, hoveredImage]);

    useEffect(() => {
      if (!highlightedMaterial) return () => {};
      return () => highlightedMaterial.dispose();
    }, [highlightedMaterial]);

    const meshes: InstancedObjectMeshData[] = useMemo(() => {
      return [{ geometry: anchorSphere, material, renderOrder: RENDER_ORDER_IMAGE_ANCHOR }];
    }, [anchorSphere, material]);

    const hoveredMeshes: InstancedObjectMeshData[] = useMemo(() => {
      return [{ geometry: anchorSphere, material: highlightedMaterial }];
    }, [anchorSphere, highlightedMaterial]);

    const instancedObject = useMemo(() => {
      return (
        <InstancedObject
          id={id ?? name}
          meshes={meshes}
          hoveredMeshes={hoveredMeshes}
          instances={anchorInstances}
          shouldHandleClick={shouldHandleClick}
        />
      );
    }, [id, name, meshes, hoveredMeshes, anchorInstances, shouldHandleClick]);

    return (
      <>
        {instancedObject}
        {hoveredImage && hoveredImage.name !== currentImage?.name && (
          <ImageBillboard position={hoveredImage.position} name={hoveredImage.name} />
        )}
      </>
    );
  }
);
